import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Profile } from 'app/modules/profile/profile.model';
import { ProfileService } from 'app/modules/profile/profile.service';
import { FuseConfigService } from '@fuse/services/config';
import { AuthService } from 'app/authentication/auth/auth.service';
import { SchemePreferenceService } from 'app/shared/scheme-preference.service';
import {
    organizationNeedsBranchSubmenu,
    resolveSwitcherBranches,
    resolveWorkspaceSwitcherMode,
    WorkspaceSwitcherMode,
} from 'app/shared/workspace-switch.util';
import { Layout } from 'app/layout/layout.types';
import { LAYOUT_OPTIONS, LayoutOption } from 'app/shared/layout-options';
import { environment } from 'environments/environment';

@Component({
    selector: 'user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'userMenu',
    standalone: false
})
export class UserMenuComponent implements OnInit, OnDestroy {
    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar: boolean = true;
    user: Profile;



    organizations: any[] = [];
    branches: any[] = [];
    selectedOrganization: any = null;
    selectedBranch: any = null;
    hoveredOrganization: any = null;
    impersonating = false;
    darkMode = true;
    currentLayout: Layout = 'classy';
    readonly layoutOptions: LayoutOption[] = LAYOUT_OPTIONS;
    layoutPickerOpen = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    e = environment;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: ProfileService,
        private _authService: AuthService,
        private _fuseConfigService: FuseConfigService,
        private _schemePreference: SchemePreferenceService
    ) {
        this.user = this._userService.user$ as any;
        const savedScheme = this._schemePreference.get();
        if (savedScheme) {
            this._fuseConfigService.config = { scheme: savedScheme };
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                const scheme = config?.scheme;
                this.darkMode = scheme === 'dark' || (scheme === 'auto' && this._prefersDark());
                if (config?.layout) {
                    this.currentLayout = config.layout as Layout;
                }
                this._changeDetectorRef.markForCheck();
            });

        this.organizations = Array.isArray(this.e.user?.organizations) ? this.e.user.organizations : [];
        this.selectedOrganization = this.e.selection?.organization ?? null;
        this.selectedBranch = this.e.selection?.branch ?? null;
        this.branches = this.selectedOrganization?.branches || [];
        this.impersonating = environment.user.impersonator != undefined;

        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: Profile) => {
                this.user = user;
                this.impersonating = environment.user.impersonator != undefined;
                this._changeDetectorRef.markForCheck();
            });

        this._userService.organizations$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((organizations: any) => {
                this.organizations = Array.isArray(organizations) ? organizations : [];
                this._changeDetectorRef.markForCheck();
            });

        this._userService.selectedOrganization$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((organization: any) => {
                this.selectedOrganization = organization;
                this.branches = organization?.branches || [];
                this._applyWorkspaceLayoutFromSelection();
                this._changeDetectorRef.markForCheck();
            });

        this._userService.selectedBranch$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branch: any) => {
                this.selectedBranch = branch;
                this._applyWorkspaceLayoutFromSelection();
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get isSuperAdmin(): boolean {
        return this.e.user?.roles?.super === true;
    }

    get displayUserId(): string {
        return this.user?.id ? `${this.user.id}` : '-';
    }


    get workspaceSwitcherMode(): WorkspaceSwitcherMode {
        if (this.isSuperAdmin) {
            return 'hidden';
        }
        return resolveWorkspaceSwitcherMode(this.organizations, undefined, this.selectedOrganization);
    }

    get showWorkspaceSwitcher(): boolean {
        return this.workspaceSwitcherMode !== 'hidden';
    }

    get switcherBranches(): any[] {
        return resolveSwitcherBranches(this.organizations, this.selectedOrganization);
    }

    get switcherOrganization(): any {
        return this.selectedOrganization ?? this.organizations[0] ?? null;
    }

    organizationNeedsBranchSubmenu(organization: any): boolean {
        return organizationNeedsBranchSubmenu(organization);
    }

    get canManageUsers(): boolean {
        return this.e.user.roles?.admin === true || this.e.user?.roles?.super === true;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        if (!this.user) {
            return;
        }

        // this._userService.set({
        //     ...this.user,
        //     status
        // }).subscribe();
    }

    /**
     * Select organization
     *
     * @param organization
     */
    selectOrganization(organization: any): void {
        const branches = organization?.branches || [];

        if (branches.length <= 1) {
            this.selectBranch(branches[0] || null, organization);
            return;
        }

        this.hoveredOrganization = organization;
        this._userService.selectedOrganization = organization;
        this._userService.selectedBranch = null;
        this._applyWorkspaceLayoutFromSelection();
    }

    /**
     * Select branch
     *
     * @param branch
     * @param organization
     */
    selectBranch(branch: any, organization: any): void {
        this._authService.applyWorkspaceSelection(organization, branch).subscribe({
            next: () => {
                this._applyWorkspaceLayoutFromSelection();
                this._changeDetectorRef.markForCheck();
            }
        });
    }

    private _applyWorkspaceLayoutFromSelection(): void {
        const organization = this.selectedOrganization ?? this.e.selection?.organization;
        const branch = this.selectedBranch ?? this.e.selection?.branch;
        const config = branch?.config || organization?.config;

        if (config) {
            this._fuseConfigService.applyPermanentLayoutChanges(config);
        }
    }

    /**
     * Set hovered organization for submenu
     *
     * @param organization
     */
    setHoveredOrganization(organization: any): void {
        this.hoveredOrganization = organization;
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Sign out
     */

openLayoutPicker(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.layoutPickerOpen = true;
        this._changeDetectorRef.markForCheck();
    }

    closeLayoutPicker(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.layoutPickerOpen = false;
        this._changeDetectorRef.markForCheck();
    }

    onUserMenuClosed(): void {
        this.layoutPickerOpen = false;
        this._changeDetectorRef.markForCheck();
    }

    get currentLayoutLabel(): string {
        return this.layoutOptions.find((o) => o.id === this.currentLayout)?.label ?? this.currentLayout;
    }

    setLayout(layout: Layout): void {
        this.layoutPickerOpen = false;
        this._router.navigate([], {
            queryParams: { layout: null },
            queryParamsHandling: 'merge',
        }).then(() => {
            this._fuseConfigService.config = { layout };
            this.currentLayout = layout;
            this._changeDetectorRef.markForCheck();
        });
    }


    onDarkModeChange(enabled: boolean): void {
        const scheme = enabled ? 'dark' : 'light';
        this._schemePreference.set(scheme);
        this._fuseConfigService.config = { scheme };
        this._changeDetectorRef.markForCheck();
    }

    signOut(): void {
        window.location.assign('/auth/sign-out');
    }

    unimpersonate(): void {
        this._authService
            .unimpersonate()
            .subscribe((ok) => {
                if (!ok) {
                    return;
                }
                this.impersonating = false;
                this._changeDetectorRef.markForCheck();
                window.location.assign('/dashboard');
            });
    }

    // private _hydrateFromLocalStorage(): void {
    //     this.user = this.e.user.profile;
    //     this.organizations = this.e.user?.organizations ?? [];
    //     this.selectedOrganization = this.e.selection.organization;
    //     this.selectedBranch = this.e.selection.branch;
    //     this.branches = this.selectedOrganization?.branches || [];
    // }

    // private _normalizeStoredUser(user: Profile | null): Profile | null {
    //     if (!user) {
    //         return null;
    //     }

    //     const asAny = user as any;
    //     const resolvedName = `${asAny?.name || asAny?.fullname || ''}`.trim();

    //     if (resolvedName && resolvedName !== asAny?.name) {
    //         return {
    //             ...user,
    //             name: resolvedName
    //         };
    //     }

    //     return user;
    // }

    private _prefersDark(): boolean {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return false;
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    // private _normalizeOrganizations(value: any): any[] {
    //     if (Array.isArray(value)) {
    //         return value;
    //     }

    //     if (value && typeof value === 'object') {
    //         return Object.values(value);
    //     }

    //     return [];
    // }

}
